import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit2, Package, Loader2, ArrowLeft,
  BookOpen, DollarSign, Users, CheckCircle2, Tag,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminBundlesPage = () => {
  const { api } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [bundleCategories, setBundleCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBundle, setEditBundle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [bundlesRes, coursesRes, catsRes] = await Promise.allSettled([
        api.get('/bundles/admin/all'),
        api.get('/courses/admin/list'),
        api.get('/bundles/categories/all'),
      ]);
      if (bundlesRes.status === 'fulfilled') setBundles(bundlesRes.value.data);
      if (coursesRes.status === 'fulfilled') setCourses(coursesRes.value.data);
      if (catsRes.status === 'fulfilled') setBundleCategories(catsRes.value.data || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => setEditBundle({
    _isNew: true, title: '', description: '', course_ids: [],
    full_price: 99, subscriber_price: 69, savings_text: 'Subscribers save 30%',
    status: 'active', image_url: '', category: '',
  });

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post('/bundles/categories', { name: newCatName.trim() });
      setNewCatName('');
      const res = await api.get('/bundles/categories/all');
      setBundleCategories(res.data || []);
      toast.success('Category added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Delete this bundle category?')) return;
    try {
      await api.delete(`/bundles/categories/${catId}`);
      setBundleCategories(prev => prev.filter(c => c.id !== catId));
      toast.success('Category deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSave = async () => {
    if (!editBundle.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editBundle._isNew) {
        await api.post('/bundles/admin', editBundle);
        toast.success('Bundle created');
      } else {
        await api.put(`/bundles/admin/${editBundle.id}`, editBundle);
        toast.success('Bundle updated');
      }
      setEditBundle(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bundle?')) return;
    try {
      await api.delete(`/bundles/admin/${id}`);
      setBundles(prev => prev.filter(b => b.id !== id));
      toast.success('Bundle deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleCourse = (courseId) => {
    setEditBundle(prev => {
      const ids = prev.course_ids || [];
      return {
        ...prev,
        course_ids: ids.includes(courseId)
          ? ids.filter(id => id !== courseId)
          : [...ids, courseId],
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-bundles-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Admin</Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#2a3e5d]">Course Bundles</h1>
              <p className="text-sm text-slate-500">{bundles.length} bundles</p>
            </div>
          </div>
          <Button onClick={openNew} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="create-bundle-btn">
            <Plus className="h-4 w-4 mr-2" /> New Bundle
          </Button>
        </div>

        {bundles.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No bundles yet. Create your first course bundle.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bundles.map(b => (
              <Card key={b.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`bundle-card-${b.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base font-semibold text-[#2a3e5d] leading-snug">{b.title}</CardTitle>
                    <div className="flex items-center gap-1.5">
                      {b.category && <Badge className="bg-[#02d1d4]/10 text-[#02d1d4] text-[10px] border-[#02d1d4]/20">{b.category}</Badge>}
                      <Badge className={b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{b.status}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-500 line-clamp-2">{b.description}</p>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-bold text-[#2a3e5d]">£{b.full_price}</span>
                    <span className="text-slate-400">/</span>
                    <span className="font-bold text-[#02d1d4]">£{b.subscriber_price}</span>
                    <span className="text-[10px] text-slate-400">subscriber</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {b.course_count || 0} courses</span>
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {b.purchase_count || 0} purchases</span>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={() => setEditBundle(b)} className="flex-1" data-testid={`edit-bundle-${b.id}`}>
                      <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(b.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Bundle Categories Management */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">
        <div className="mt-10 pt-8 border-t border-slate-200">
          <h2 className="text-lg font-bold text-[#2a3e5d] mb-1">Bundle Categories</h2>
          <p className="text-sm text-slate-500 mb-4">Organise bundles into categories for the pricing page</p>
          
          <div className="flex gap-2 mb-4">
            <Input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="New category name (e.g. Safeguarding)"
              className="max-w-xs"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }}
              data-testid="bundle-cat-input"
            />
            <Button onClick={handleAddCategory} size="sm" className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="add-bundle-cat-btn">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {bundleCategories.map(cat => (
              <div key={cat.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm" data-testid={`bundle-cat-${cat.id}`}>
                <Tag className="h-3.5 w-3.5 text-[#02d1d4]" />
                <span className="text-slate-700">{cat.name}</span>
                <button onClick={() => handleDeleteCategory(cat.id)} className="text-slate-400 hover:text-red-500 ml-1">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {bundleCategories.length === 0 && (
              <p className="text-sm text-slate-400 italic">No bundle categories yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={!!editBundle} onOpenChange={() => setEditBundle(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bundle-edit-modal">
          <DialogHeader>
            <DialogTitle className="text-[#2a3e5d]">{editBundle?._isNew ? 'Create Bundle' : 'Edit Bundle'}</DialogTitle>
            <DialogDescription>Configure the bundle details and select courses to include.</DialogDescription>
          </DialogHeader>
          {editBundle && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Bundle Title *</Label>
                <Input value={editBundle.title} onChange={(e) => setEditBundle(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g. Safeguarding Essentials Bundle" data-testid="bundle-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Description</Label>
                <Textarea value={editBundle.description} onChange={(e) => setEditBundle(prev => ({ ...prev, description: e.target.value }))} placeholder="Describe what this bundle includes..." rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Full Price (£)</Label>
                  <Input type="number" min="0" step="0.01" value={editBundle.full_price} onChange={(e) => setEditBundle(prev => ({ ...prev, full_price: parseFloat(e.target.value) || 0 }))} data-testid="bundle-full-price" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Subscriber Price (£)</Label>
                  <Input type="number" min="0" step="0.01" value={editBundle.subscriber_price} onChange={(e) => setEditBundle(prev => ({ ...prev, subscriber_price: parseFloat(e.target.value) || 0 }))} data-testid="bundle-sub-price" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Savings Text</Label>
                <Input value={editBundle.savings_text} onChange={(e) => setEditBundle(prev => ({ ...prev, savings_text: e.target.value }))} placeholder="e.g. Subscribers save 30%" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Bundle Category</Label>
                <select
                  value={editBundle.category || ''}
                  onChange={(e) => setEditBundle(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20acf6]"
                  data-testid="bundle-category-select"
                >
                  <option value="">No category</option>
                  {bundleCategories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Active</Label>
                <Switch checked={editBundle.status === 'active'} onCheckedChange={(v) => setEditBundle(prev => ({ ...prev, status: v ? 'active' : 'inactive' }))} />
              </div>

              {/* Course Selection */}
              <div className="space-y-2">
                <Label className="text-sm">Courses in Bundle ({(editBundle.course_ids || []).length} selected)</Label>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {courses.map(c => {
                    const selected = (editBundle.course_ids || []).includes(c.id);
                    return (
                      <label key={c.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${selected ? 'bg-[#20acf6]/5' : ''}`}>
                        <input type="checkbox" checked={selected} onChange={() => toggleCourse(c.id)} className="accent-[#20acf6] h-4 w-4" />
                        <span className="text-sm text-slate-700 flex-1">{c.title}</span>
                        {c.price > 0 && <span className="text-xs text-slate-400">£{c.price}</span>}
                        <Badge className="text-[10px]" variant="outline">{c.status}</Badge>
                      </label>
                    );
                  })}
                  {courses.length === 0 && <p className="p-3 text-sm text-slate-400 italic">No courses found</p>}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving} className="bg-[#20acf6] hover:bg-[#3bb8f7] flex-1" data-testid="save-bundle-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save Bundle'}
                </Button>
                <Button variant="outline" onClick={() => setEditBundle(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBundlesPage;
